import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormHelperText,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { sendEmailAction } from '../../../redux/actions/communicationAction';
import ModernRichTextEditor from './ModernRichTextEditor'; // Import the new editor

const SendEmailDialog = ({ open, onClose, policyId: defaultPolicyId, clientCustomerId }) => {
  const dispatch = useDispatch();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [policyId, setPolicyId] = useState(defaultPolicyId || '');
  const [selectedOption, setSelectedOption] = useState(defaultPolicyId ? 'policy' : 'customer');
  const [errors, setErrors] = useState({ subject: '', body: '', policyId: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const selected_customer_list = useSelector(
    (state) => state?.globalSearchReducer?.customer_data?.content || []
  );

  const validate = () => {
    const newErrors = { subject: '', body: '', policyId: '' };
    if (!subject.trim()) newErrors.subject = 'Subject is required';

    // Better validation for rich text content
    const bodyText = body.replace(/<[^>]*>/g, '').trim();
    if (!bodyText || bodyText === '' || body.trim() === '<p></p>' || body.trim() === '<br><br>') {
      newErrors.body = 'Message body is required';
    }

    if (selectedOption === 'policy' && !policyId) newErrors.policyId = 'Policy is required';
    setErrors(newErrors);
    return !newErrors.subject && !newErrors.body && (!newErrors.policyId || selectedOption === 'customer');
  };

  const handleSend = () => {
    if (!validate()) return;

    const payload = {
      subject,
      body,
      ...(selectedOption === 'policy' ? { policyId } : { clientCustomerId }),
    };

    dispatch(
      sendEmailAction(payload, () => {
        setSnackbar({
          open: true,
          message: 'Email sent successfully!',
          severity: 'success'
        });
      setTimeout(() => {
        handleClose();
      }, 2000); // Close after 2 seconds

      })
    );
  };

  const handleClose = () => {
    setSubject('');
    setBody('');
    setPolicyId(defaultPolicyId || '');
    setSelectedOption(defaultPolicyId ? 'policy' : 'customer');
    setErrors({ subject: '', body: '', policyId: '' });
    setSnackbar({ open: false, message: '', severity: 'success' });
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Dialog open={open} onClose={(event, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") { return; } handleClose(event, reason); }}
            maxWidth="lg" fullWidth disableScrollLock={true} keepMounted={false} hideBackdrop={false} style={{ zIndex: 1300 }}>
      <DialogTitle
        className="btn-primary"
        sx={{
          height: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          fontWeight: 600,
        }}
      >
        Send Email
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3} mt={1}>
          {/* Subject */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                fontSize: '14px',
                color: '#333',
              }}
            >
              Subject <span style={{ color: '#f44336' }}>*</span>
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              error={!!errors.subject}
              helperText={errors.subject}
              placeholder="Enter email subject"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#ffae00',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '2px',
                  },
                },
              }}
            />
          </Box>

          {/* Radio Group */}
          <FormControl>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: '14px',
                color: '#333',
              }}
            >
              Send To
            </Typography>
            <RadioGroup
              row
              value={selectedOption}
              onChange={(e) => setSelectedOption(e.target.value)}
              sx={{
                '& .MuiFormControlLabel-root': {
                  mr: 4,
                },
              }}
            >
              <FormControlLabel
                value="customer"
                control={<Radio size="small" />}
                label="Customer"
              />
              <FormControlLabel
                value="policy"
                control={<Radio size="small" />}
                label="Policy"
              />
            </RadioGroup>
          </FormControl>

          {/* Policy Select */}
          {selectedOption === 'policy' && (
            <FormControl fullWidth error={!!errors.policyId}>
              <InputLabel>Select Policy Number</InputLabel>
              <Select
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
                label="Select Policy Number"
                sx={{
                  borderRadius: '8px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976d2',
                  },
                }}
              >
                {selected_customer_list.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.policyNumber}
                  </MenuItem>
                ))}
              </Select>
              {errors.policyId && <FormHelperText>{errors.policyId}</FormHelperText>}
            </FormControl>
          )}

          {/* Message Body */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                fontSize: '14px',
                color: '#333',
              }}
            >
              Message <span style={{ color: '#f44336' }}>*</span>
            </Typography>
            <ModernRichTextEditor
              content={body}
              onChange={setBody}
              error={!!errors.body}
            />
            {errors.body && (
              <FormHelperText error sx={{ mt: 1, ml: 0 }}>
                {errors.body}
              </FormHelperText>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          className="btn-secondary"
          sx={{textTransform: 'none', fontSize: '15px'}}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          className="btn-secondary"
          color="primary"
          sx={{textTransform: 'none', fontSize: '15px', marginRight: '13px'}}
        >
          Send Email
        </Button>
      </DialogActions>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            borderRadius: '8px',
            fontWeight: 500,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default SendEmailDialog;